import React, { useState, useEffect, useContext } from "react";
import {
  ThemeContext,
  PatientContext,
  UserContext,
} from "../../lib/main-context";
import { useTranslation } from "../../i18n/TranslationProvider";

import { useHistory, Link } from "react-router-dom";

import Form from "react-bootstrap/Form";
import { useForm } from "react-hook-form";
import { format, compareDesc } from "date-fns";

import theme from "../../index.scss";
import LabelledInput from "../UIComponents/LabelledInput";
import SubmitButton from "../UIComponents/SubmitButton";

import { sendWarnEmailToProviderAndTP } from "../../api/helpReq";
import { postTrackSymptom } from "../../api/TBIRequests";
import getLoggedDataToday from "../../lib/getLoggedDataToday";
import TopThreeSymptomsPreview from "./TopThreeSymptomsPreview";
import AllSymptomsPreview from "./AllSymptomsPreview";
import useWindowSize from "../../lib/useWindowSize";

const SymptomTracker = ({
  currentTrackingStep,
  topSymptoms,
  worstSymptoms,
  date,
  symptom,
  trackedData,
  skipTrackingStep,
  saveTrackingStep,
  finishTop3TrackingStep,
  loadTrackedData,
  trackingMode,
  setTrackingMode,
  top3Step,
  setTop3Step,
}) => {
  const history = useHistory();

  const [showNotes, setShowNotes] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [showImprove, setShowImprove] = useState(false);
  const [showBigImprove, setShowBigImprove] = useState(false);
  const [severitySymptom, setSeveritySymptom] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const { dark } = useContext(ThemeContext);
  const { user } = useContext(UserContext);
  const { patient } = useContext(PatientContext);
  const { t } = useTranslation();

  // Mobile breakpoint matches Dashboard.js (width >= 768 = desktop layout)
  const [width] = useWindowSize();
  const isMobile = width < 768;

  const tSymptom = (factor) => {
    const categories = ["cognitive","sleep","emotional","physical","speech","vision"];
    const safe = (factor || "").trim().toLowerCase()
      .replace(/ /g,"_").replace(/\//g,"_").replace(/,/g,"")
      .replace(/:/g,"").replace(/'/g,"").replace(/[()]/g,"").replace(/-/g,"_")
      .substring(0, 60);
    for (const cat of categories) {
      const key = `symptom.${cat}.${safe}`;
      const translated = t(key);
      if (translated !== key) return translated;
    }
    return factor;
  };

  const { register, handleSubmit, errors, setError, reset } = useForm();

  // Use worstSymptoms for top3 mode, fall back to topSymptoms if not loaded yet
  const top3List = worstSymptoms && worstSymptoms.length > 0 ? worstSymptoms : topSymptoms;

  // In top3 mode, drive symptom from top3List[top3Step]; otherwise use the prop
  const activeSymptom = trackingMode === "top3" ? top3List[top3Step] : symptom;

  console.log(
    "[SYMPTOM-TRACKER render] trackingMode=",
    trackingMode,
    "top3Step=",
    top3Step,
    "top3List.length=",
    top3List && top3List.length,
    "top3List source=",
    worstSymptoms && worstSymptoms.length > 0 ? "worstSymptoms" : "topSymptoms(fallback)",
    "activeSymptom=",
    activeSymptom && activeSymptom.factor,
    "incoming symptom prop=",
    symptom && symptom.factor
  );

  let severityColor;
  if (severitySymptom <= 33) {
    severityColor = "#28a745"; // green
  } else if (severitySymptom <= 66) {
    severityColor = "#ffc107"; // yellow
  } else {
    severityColor = "#dc3545"; // red
  }

  const dataset = trackedData.filter(
    (item) => item.factor === (activeSymptom && activeSymptom.factor)
  );

  let prevIntensity;
  if (dataset && dataset.length > 0) {
    for (let i = dataset.length - 1; i >= 0; i--) {
      let isPrevDate = compareDesc(
        new Date(dataset[i].symptom_date),
        new Date(date)
      );
      if (isPrevDate === 1 && !prevIntensity) {
        prevIntensity = dataset[i].severity;
      }
    }
  } else {
    prevIntensity = null;
  }

  useEffect(() => {
    if (prevIntensity) {
      const currIntensity = parseInt(severitySymptom, 10);

      setShowNotes(false);
      setShowWarning(false);
      setShowImprove(false);
      setShowBigImprove(false);

      if (
        currIntensity >= prevIntensity + 10 &&
        currIntensity < prevIntensity + 30
      ) {
        setShowNotes(true);
      } else if (currIntensity >= prevIntensity + 30) {
        setShowWarning(true);
      } else if (currIntensity + 30 <= prevIntensity && currIntensity !== 0) {
        setShowBigImprove(true);
      } else if (currIntensity + 10 <= prevIntensity && currIntensity !== 0) {
        setShowImprove(true);
      }
    }
  }, [prevIntensity, severitySymptom]);

  // Self-healing correction for top3Step. top3Step is otherwise just a
  // counter incremented on each submit, which desyncs if worstSymptoms
  // changes mid-session (e.g. an Add/Pause replace swaps one of the 3 slots
  // out). Recompute the position of the first not-yet-logged item in the
  // CURRENT top3List, and correct top3Step to that position whenever it
  // disagrees. This guarantees a newly swapped-in symptom always gets
  // presented for tracking instead of being silently skipped past.
  //
  // NOTE: this must be a *position* (first unlogged index), not a raw count
  // of how many are logged. A raw count only happens to equal the correct
  // index when the logged items are exactly the leading N entries in the
  // array — true when slot 2 (the last) gets replaced, false when slot 0 or
  // 1 does, since the logged items are then no longer contiguous from the
  // start. Using the count as an index in that case lands on whichever item
  // sits at that position, logged or not — which is why replacing the last
  // slot worked but replacing slot 0/1 kept re-presenting an already-logged
  // symptom instead of the actual new one.
  useEffect(() => {
    if (trackingMode !== "top3" || !top3List || top3List.length === 0) return;

    const loggedFactorsToday = new Set(
      getLoggedDataToday(trackedData, "medical", date).map(
        (item) => item.factor
      )
    );

    const firstUnloggedIndex = top3List.findIndex(
      (s) => !loggedFactorsToday.has(s.factor)
    );
    // -1 means every item in top3List is already logged — treat that the
    // same way as "step past the end" so downstream completion logic
    // (top3Step === 3) still fires normally.
    const correctedStep = firstUnloggedIndex === -1 ? top3List.length : firstUnloggedIndex;

    if (correctedStep !== top3Step) {
      console.log(
        "[SYMPTOM-TRACKER top3-self-correct] top3Step was",
        top3Step,
        "-> correcting to",
        correctedStep,
        "(first unlogged position in top3List, not a logged-count)"
      );
      setTop3Step(correctedStep);
    }
  }, [trackingMode, top3List, trackedData, date]);

  // Defensive guard: correctedStep above can legitimately equal
  // top3List.length when every top-3 item is already logged (e.g. the
  // moment before Dashboard transitions away from top3 mode). In that
  // instant there is no top3List[top3Step] to show — activeSymptom is
  // undefined. Rather than let the render below crash on
  // activeSymptom.factor, treat "nothing left to show" as "session
  // complete" and finish the same way a normal 3rd submission would.
  // Only fires once per occurrence since finishTop3TrackingStep resets
  // trackingMode to null, which immediately fails this effect's own guard.
  useEffect(() => {
    if (trackingMode === "top3" && top3List && top3List.length > 0 && !top3List[top3Step]) {
      console.log(
        "[SYMPTOM-TRACKER top3-out-of-bounds] top3Step=",
        top3Step,
        "top3List.length=",
        top3List.length,
        "-> no activeSymptom, finishing top3 session instead of rendering undefined"
      );
      finishTop3TrackingStep();
    }
  }, [trackingMode, top3List, top3Step]);

  const onSubmit = async (data) => {
    setSubmitting(true);

    data.severitySymptom = severitySymptom;

    if (showWarning) {
      const picked = { factor: activeSymptom.factor };
      const processedData = [
        {
          ...data,
          symptom: picked,
          datetimeOfSymptom: format(date, "yyyy-MM-dd"),
        },
      ];
      try {
        await sendWarnEmailToProviderAndTP({
          patientId: patient.patientId,
          data: processedData,
        });
      } catch (err) {
        console.warn("sendProviderWarnEmail error", err);
      }
    }

    const picked = {
      factor: activeSymptom.factor,
      category: activeSymptom.category,
      subcategory: activeSymptom.subcategory,
    };
    const processedData = [
      {
        ...data,
        symptom: picked,
        datetimeOfSymptom: format(date, "yyyy-MM-dd"),
        hadSymptom: true,
      },
    ];
    try {
      await postTrackSymptom({
        patientId: patient.patientId,
        data: processedData,
      });
      reset();
      setSeveritySymptom(0);

      if (trackingMode === "top3") {
        const nextStep = top3Step + 1;
        console.log(
          "[SYMPTOM-TRACKER onSubmit] top3 submit complete. just submitted=",
          activeSymptom && activeSymptom.factor,
          "old top3Step=",
          top3Step,
          "nextStep=",
          nextStep
        );
        if (nextStep < 3) {
          console.log("[SYMPTOM-TRACKER onSubmit] -> setTop3Step(", nextStep, ") + loadTrackedData()");
          setTop3Step(nextStep);
          setSubmitting(false);
          loadTrackedData();
        } else {
          console.log("[SYMPTOM-TRACKER onSubmit] -> all 3 done, calling finishTop3TrackingStep() + loadTrackedData()");
          finishTop3TrackingStep();
          loadTrackedData();
        }
      } else {
        saveTrackingStep();
        loadTrackedData();
      }
    } catch (err) {
      history.push("/oops");
    }
  };

  if (!symptom || topSymptoms.length == 0) {
    return (
      <div>
        {t("symptomtracker.no_symptoms.0")}
        <Link
          to={{
            pathname: "laterSymptoms",
            state: { fromPatientControl: false, fromPatientDashboard: true },
          }}
        >
          {t("symptomtracker.no_symptoms.1")}
        </Link>
      </div>
    );
  }

  // Same out-of-bounds case the effect above handles: top3Step pointing
  // past the end of top3List means activeSymptom is undefined. The effect
  // will call finishTop3TrackingStep() and Dashboard will move on next
  // render — render nothing for this single transient frame rather than
  // crash on activeSymptom.factor below.
  if (trackingMode === "top3" && !activeSymptom) {
    return null;
  }

  return (
    <>
      <h6 className="d-flex justify-content-between">
        <span>
          {patient.userPatientRelationshipType == "self"
            ? t("tracksymptom.patient")
            : t("tracksymptom.non_patient.0") +
              patient.firstName +
              t("tracksymptom.non_patient.1")}
        </span>
        <span>{format(date, "LLL d, yyyy")}</span>
      </h6>

      {/* Mode selector — shown until the user picks a mode */}
      {trackingMode === null && (
        <div
          className="d-flex"
          style={{
            margin: "1rem 0",
            width: "100%",
            minWidth: 0,
            // Mobile: stack Top 3 (button + pills) above Track All (button + categories)
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? "1.5rem" : 0,
          }}
        >
          <div
            className="d-flex flex-column align-items-center"
            style={{
              flex: 1,
              minWidth: 0,
              padding: isMobile ? 0 : "0 1rem",
              boxSizing: "border-box",
            }}
          >
            <SubmitButton
              type="button"
              style={{
                whiteSpace: isMobile ? "normal" : "nowrap",
                width: isMobile ? "100%" : undefined,
              }}
              onClick={() => {
                console.log("[MODE-SELECT] user clicked 'Track Top 3 Worst Symptoms'");
                setTrackingMode("top3");
                setTop3Step(0);
              }}
            >
              {t("symptomtracker.mode.top3")}
            </SubmitButton>
            <TopThreeSymptomsPreview
              worstSymptoms={worstSymptoms}
              topSymptoms={topSymptoms}
            />
          </div>
          <div
            className="d-flex flex-column align-items-center"
            style={{
              flex: 1,
              minWidth: 0,
              padding: isMobile ? 0 : "0 1rem",
              boxSizing: "border-box",
            }}
          >
            <SubmitButton
              type="button"
              style={{
                whiteSpace: isMobile ? "normal" : "nowrap",
                width: isMobile ? "100%" : undefined,
              }}
              onClick={() => setTrackingMode("all")}
            >
              {t("symptomtracker.mode.all")}
            </SubmitButton>
            <AllSymptomsPreview topSymptoms={topSymptoms} />
          </div>
        </div>
      )}

      {/* Tracker form — shown once a mode is selected */}
      {trackingMode !== null && (
        <>
          {/* inner box */}
          <div
            className="p-3 m-3"
            style={{
              borderRadius: "5px",
              boxShadow: "0px 0px 2px 1px rgba(0,0,0,0.75)",
            }}
          >
            <div className="d-flex justify-content-center">
              <h6
                className="pt-2 pb-2 pl-3 pr-3"
                style={{
                  width: "50%",
                  borderRadius: "20px",
                  backgroundColor: dark ? theme.darkModePrimary : theme.primary,
                  color: dark ? theme.darkModeText : "white",
                  textAlign: "center",
                  overflowWrap: "break-word",
                }}
              >
                {tSymptom(activeSymptom.factor)}
              </h6>
            </div>
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Form.Label style={{ width: "100%" }}>
                {t("symptomtracker.intensity_level.label")}
                {severitySymptom}
                <Form.Control
                  name="severitySymptom"
                  ref={register}
                  type="range"
                  min="0"
                  max="100"
                  className="severity-range"
                  value={severitySymptom}
                  onChange={(e) => setSeveritySymptom(e.target.value)}
                  style={{
                    "--severity-color": severityColor,
                    "--severity-val": `${severitySymptom}%`,
                  }}
                />
                <div
                  style={{ fontSize: "14px" }}
                  className="d-flex justify-content-between"
                >
                  <span>{t("symptomtracker.intensity_level.no")}</span>
                  <span style={{ textAlign: "right" }}>
                    {t("symptomtracker.intensity_level.worst")}
                  </span>
                </div>
              </Form.Label>

              {(showNotes || showImprove || showBigImprove || showWarning) && (
                <>
                  {showWarning ? (
                    <div
                      className="p-3"
                      style={{ border: "1px solid red", borderRadius: "5px" }}
                    >
                      {user.userType == "caregiver"
                        ? t("symptomtracker.messages.big_increase.caregiver.0") +
                          patient.firstName +
                          t("symptomtracker.messages.big_increase.caregiver.1") +
                          activeSymptom.factor +
                          t("symptomtracker.messages.big_increase.caregiver.2")
                        : t("symptomtracker.messages.big_increase.patient.0") +
                          activeSymptom.factor +
                          t("symptomtracker.messages.big_increase.patient.1")}
                      <Link
                        to={{
                          pathname: "/warning",
                          state: { symtom: activeSymptom.factor },
                        }}
                      >
                        {t("symptomtracker.messages.big_increase.learn_more")}
                      </Link>
                    </div>
                  ) : showBigImprove ? (
                    <div
                      className="p-3"
                      style={{ border: "1px solid blue", borderRadius: "5px" }}
                    >
                      {user.userType == "caregiver"
                        ? t("symptomtracker.messages.big_improve.caregiver.0") +
                          patient.firstName +
                          t("symptomtracker.messages.improve.caregiver.1")
                        : t("symptomtracker.messages.big_improve.patient")}
                    </div>
                  ) : showImprove ? (
                    user.userType == "caregiver" ? (
                      <h6>
                        {t("symptomtracker.messages.improve.caregiver.0")}
                        {patient.firstName}
                        {t("symptomtracker.messages.improve.caregiver.1")}
                      </h6>
                    ) : (
                      <h6>{t("symptomtracker.messages.improve.patient")}</h6>
                    )
                  ) : (
                    <h6>{t("symptomtracker.messages.increase")}</h6>
                  )}

                  <LabelledInput
                    label={t("symptomtracker.note")}
                    name="describeSymptom"
                    errors={errors}
                    inputRef={register}
                    inputAs="textarea"
                  />
                </>
              )}

              <Form.Row
                style={{ flexGrow: "1", alignItems: "flex-end" }}
                className="d-flex justify-content-between"
              >
                {/* Skip only available in "all" mode — top3 cycles automatically */}
                {trackingMode === "all" && (
                  <SubmitButton
                    onClick={() => {
                      reset();
                      setSeveritySymptom(0);
                      skipTrackingStep();
                    }}
                    style={{ height: "max-content" }}
                    type="button"
                  >
                    {t("symptomtracker.skip")}
                  </SubmitButton>
                )}

                {trackingMode === "top3"
                  ? `${top3Step + 1}/3${t("symptomtracker.total_symptoms")}`
                  : `${currentTrackingStep + 1}/${topSymptoms.length}${t("symptomtracker.total_symptoms")}`}

                <SubmitButton
                  style={{ height: "max-content" }}
                  type="submit"
                  disabled={submitting}
                >
                  {t("symptomtracker.save")}
                </SubmitButton>
              </Form.Row>
            </Form>
          </div>
        </>
      )}
    </>
  );
};

export default SymptomTracker;